import { useCallback, useContext, useMemo } from 'react';

import { trackEventMixpanel } from 'utils/mixpanel';
import { FilterContextData } from 'contexts/FilterContext';
import { PPA_WHITESPACE_AXIS_CHANGED } from 'utils/mixpanelEventNames';
import { buildWhitespaceGlobalFiltersApplied, computeWhitespaceGridMetrics } from './whitespaceMixpanel.helpers';

import useWhitespaceMixpanelEffects from './useWhitespaceMixpanelEffects';
import type { WhitespaceGridRow } from 'hooks/useWhitespaceAnalysisGridData';

type UseWhitespaceMixpanelParams = {
    isMetaLoading: boolean;
    isGridFetched: boolean;
    isGridFetching: boolean;
    xAxis: string;
    yAxis: string;
    crossSection: string;
    showWhitespaces: boolean;
    columnHeaders: string[];
    gridRows: WhitespaceGridRow[];
    totalColumnCount: number;
    totalRowCount: number;
    isFetchingMoreRows: boolean;
    isFetchingMoreColumns: boolean;
    showNoFilterSelection: boolean;
};

const useWhitespaceMixpanel = ({
    isMetaLoading,
    isGridFetched,
    isGridFetching,
    xAxis,
    yAxis,
    crossSection,
    showWhitespaces,
    columnHeaders,
    gridRows,
    totalColumnCount,
    totalRowCount,
    isFetchingMoreRows,
    isFetchingMoreColumns,
    showNoFilterSelection,
}: UseWhitespaceMixpanelParams) => {
    const { allCheckedAttribute, timeSelectionLabel, timePeriodList } = useContext(FilterContextData);

    const gridMetrics = useMemo(
        () => computeWhitespaceGridMetrics(gridRows, columnHeaders),
        [columnHeaders, gridRows],
    );

    const globalFiltersApplied = useMemo(
        () =>
            buildWhitespaceGlobalFiltersApplied({
                timeSelectionLabel,
                timePeriodList,
                allCheckedAttribute,
            }),
        [allCheckedAttribute, timePeriodList, timeSelectionLabel],
    );

    useWhitespaceMixpanelEffects({
        isMetaLoading,
        isGridFetched,
        isGridFetching,
        xAxis,
        yAxis,
        crossSection,
        showWhitespaces,
        columnCount: columnHeaders.length,
        rowCount: gridRows.length,
        totalColumnCount,
        totalRowCount,
        isFetchingMoreRows,
        isFetchingMoreColumns,
        gridMetrics,
        globalFiltersApplied,
        showNoFilterSelection,
    });

    const trackAxisChanged = useCallback(
        (next: { xAxis?: string; yAxis?: string; crossSection?: string }) => {
            trackEventMixpanel(PPA_WHITESPACE_AXIS_CHANGED, {
                previous_x_axis: xAxis,
                selected_x_axis: next.xAxis ?? xAxis,
                previous_y_axis: yAxis,
                selected_y_axis: next.yAxis ?? yAxis,
                previous_cross_section: crossSection,
                selected_cross_section: next.crossSection ?? crossSection,
            });
        },
        [crossSection, xAxis, yAxis],
    );

    return { gridMetrics, trackAxisChanged };
};

export default useWhitespaceMixpanel;
